import { getEnv } from "../config/env";
import { DEFAULT_CATEGORY_QUERY_THRESHOLD } from "../utils";

// Mirrors the helper used in AppApiService
const ensureArray = (value) => {
	if (Array.isArray(value))
		return value.filter((v) => v !== null && v !== undefined && v !== "");
	if (value === null || value === undefined || value === "") return [];
	return [value];
};

// Columns whose threshold the API expects scaled by 10^6 (USD / CPM fields)
const USD_CPM_COLUMNS = new Set([
	"nonzerobidsvalueinusd",
	"bidspendinusd",
	"revenueperimpression",
	"costperimpression",
	"bid_guidance",
	"bidCpm",
]);

// query[].threshold must reach the API as a number in [0, 1] — chips can carry
// a string if the value came from an NLP payload or a raw input event.
const toQueryThreshold = (value) => {
	const parsed = parseFloat(value);
	if (isNaN(parsed)) return 0;
	return Math.min(1, Math.max(0, parsed));
};

const mapQueries = (entries = []) =>
	entries
		.filter((entry) => entry && String(entry.query ?? "").trim() !== "")
		.map(({ query, threshold }) => ({
			query: String(query).trim(),
			threshold: toQueryThreshold(threshold),
		}));

const convertReachToNumber = (reachValue) => {
	if (!reachValue) return 0;
	if (typeof reachValue === "string") {
		const v = reachValue.toLowerCase();
		if (v.includes("m")) return parseFloat(v) * 1000000;
		if (v.includes("k")) return parseFloat(v) * 1000;
		return parseFloat(v) || 0;
	}
	return parseFloat(reachValue) || 0;
};

const getPeriodDays = (filters) => {
	const raw = filters.period ?? filters.dateRange ?? filters.noOfDays;
	const parsed = parseInt(raw, 10);
	return !isNaN(parsed) && parsed > 0 ? parsed : 30;
};

// Build performance.performanceData from the UI customFilters array
const buildPerformanceData = (filters) => {
	const out = [];
	if (Array.isArray(filters.customFilters)) {
		filters.customFilters.forEach((cf) => {
			const column = cf.column || cf.filterColumn;
			const rawThreshold = cf.threshold ?? cf.filterThreshold;
			if (column && rawThreshold !== undefined && rawThreshold !== "") {
				let threshold = parseFloat(rawThreshold);
				if (!isNaN(threshold)) {
					if (USD_CPM_COLUMNS.has(column)) threshold = threshold * 1000000;
					out.push({ filterColumn: column, filterThreshold: threshold });
				}
			}
		});
	}
	if (out.length === 0) {
		out.push({ filterColumn: "cprmarketrequests", filterThreshold: 10000 });
		out.push({ filterColumn: "impressions", filterThreshold: 100 });
	}
	return out;
};

const parseNumericOrNull = (value) => {
	if (value === null || value === undefined || value === "") return null;
	const parsed = parseFloat(value);
	return isNaN(parsed) ? null : parsed;
};

const parseTriState = (value) => {
	if (value === true || value === "true") return true;
	if (value === false || value === "false") return false;
	return null;
};

const parsePaid = (value) => {
	if (value === true || value === "paid" || value === "Paid") return true;
	if (value === false || value === "free" || value === "Free") return false;
	return null;
};

// The Category tab reuses the domain FiltersSidebar, whose distributionChannel
// default is ["Site"]. App inventory lives on the "App" channel, so anything
// site/web-flavoured is mapped over and an empty selection falls back to ["App"].
const toAppDistributionChannel = (values) => {
	const channelMap = { Site: "App", Web: "App", "Mobile App": "App", "In-App": "App" };
	const mapped = ensureArray(values).map((c) => channelMap[c] ?? c);
	const deduped = [...new Set(mapped)];
	return deduped.length > 0 ? deduped : ["App"];
};

// appfilters block expected by /api/app-data/* — same structure AppApiService sends.
// The Category tab reuses the domain FiltersSidebar, so most keys resolve to empty,
// but the block must still be present and fully shaped for the API.
const buildAppFilters = (filters = {}) => ({
	subcategory: ensureArray(filters.subcategory || filters.appSubcategory),
	developer: ensureArray(filters.developer || filters.developers),
	content_rating: ensureArray(filters.content_rating || filters.contentRating),
	keywords: ensureArray(filters.appKeywords || filters.keywords),
	rating: parseNumericOrNull(filters.rating ?? filters.appRating),
	rating_count: parseNumericOrNull(filters.rating_count ?? filters.minRatingCount) ?? 0,
	downloads: parseNumericOrNull(filters.downloads ?? filters.appDownloads),
	update_date: filters.update_date ?? null,
	languages: ensureArray(filters.languages),
	regions: ensureArray(filters.regions || filters.appRegions),
	market_source: ensureArray(filters.market_source || filters.marketSource),
	market_status: filters.market_status ?? null,
	name: ensureArray(filters.appNames || filters.name),
	paid: parsePaid(filters.isPaid !== undefined ? filters.isPaid : filters.paid),
	origin: ensureArray(filters.origin),
	creator: ensureArray(filters.creator),
	category: ensureArray(filters.category || filters.categories),
	platform: ensureArray(filters.platform),
	appinfo_content_rating: ensureArray(
		filters.appinfo_content_rating || filters.contentRating,
	),
	coppa: parseTriState(filters.coppa),
	deleted: parseTriState(filters.deleted),
	modified_datetime: filters.modified_datetime ?? null,
});

// Map the Domain-UI filter shape → the category-search API Filters DTO
// (identical structure to the domain /search payload)
const mapUiFiltersToApi = (filters = {}) => {
	const relevancePct =
		filters.relevanceThreshold ?? filters.relevance ?? 75;
	const iabCategoryScore =
		typeof relevancePct === "number" ? relevancePct / 100 : 0.75;

	const uploaded = filters.uploadedDomains || {};

	return {
		performance: {
			minViewability: (parseFloat(filters.minViewability) || 0) / 100,
			minReach: convertReachToNumber(
				filters.minReach ?? filters.expectedReach,
			),
			performanceData: buildPerformanceData(filters),
		},
		domainFilters: {
			geography: ensureArray(filters.selectedCountries || filters.geography),
			contentStyle: ensureArray(filters.contentStyle),
			videoplcmt: ensureArray(filters.videoPlacement || filters.videoplcmt),
			distributionChannel: ensureArray(filters.distributionChannel),
			deviceTypes: ensureArray(filters.deviceTypes),
			mediaSubtype: ensureArray(filters.mediaSubtype),
			publisher: ensureArray(filters.publishers || filters.publisher),
			isDirect: filters.isDirect ?? null,
		},
		vectorFilters: {
			safetyTier: ensureArray(filters.safetyTier),
			content_style: ensureArray(filters.contentStyle),
			iab_category: ensureArray(filters.iab_category || filters.categories),
			tier_Details: filters.tier_Details ?? { tier: 0, keys: [] },
			categoryThreshold: {
				iab_category_score: iabCategoryScore,
				safety_tier_cscore: filters.categoryThreshold?.safety_tier_cscore ?? 0,
				content_style_cscore:
					filters.categoryThreshold?.content_style_cscore ?? 0,
			},
		},
		cumulativeDataFilters: {
			perfomanceData:
				filters.cumulativeDataFilters?.perfomanceData ?? "avg_viewability",
			reachThreashold: filters.cumulativeDataFilters?.reachThreashold ?? 0,
			marketThreashold:
				filters.cumulativeDataFilters?.marketThreashold ?? 700000000000,
		},
		startIndex: filters.startIndex ?? 0,
		noOfResult: filters.limit ?? filters.noOfResult ?? 1000,
		noOfDays: getPeriodDays(filters),
		includeDomains: ensureArray(
			filters.includeDomains || uploaded.includeList,
		),
		excludeDomains: ensureArray(
			filters.excludeDomains || uploaded.excludeList,
		),
	};
};

// Map the UI filter shape → the /api/app-data/category-search Filters DTO.
// The app endpoint's DTO differs from the domain one:
//   - has `appfilters`
//   - has NO `vectorFilters`; `tier_Details` sits at the filters root
//   - has NO `startIndex`
//   - uses includeBundles/excludeBundles instead of includeDomains/excludeDomains
const mapUiFiltersToAppApi = (filters = {}) => {
	const uploaded = filters.uploadedBundles || filters.uploadedDomains || {};

	return {
		performance: {
			minViewability: (parseFloat(filters.minViewability) || 0) / 100,
			minReach: convertReachToNumber(
				filters.minReach ?? filters.expectedReach,
			),
			performanceData: buildPerformanceData(filters),
		},
		domainFilters: {
			geography: ensureArray(filters.selectedCountries || filters.geography),
			contentStyle: ensureArray(filters.contentStyle),
			videoplcmt: ensureArray(filters.videoPlacement || filters.videoplcmt),
			distributionChannel: toAppDistributionChannel(filters.distributionChannel),
			deviceTypes: ensureArray(filters.deviceTypes),
			mediaSubtype: ensureArray(filters.mediaSubtype),
			publisher: ensureArray(filters.publishers || filters.publisher),
			isDirect: filters.isDirect ?? null,
		},
		appfilters: buildAppFilters(filters),
		noOfResult: filters.limit ?? filters.noOfResult ?? 1000,
		noOfDays: getPeriodDays(filters),
		tier_Details: filters.tier_Details ?? { tier: 0, keys: [] },
		cumulativeDataFilters: {
			perfomanceData:
				filters.cumulativeDataFilters?.perfomanceData ?? "total_impressions",
			reachThreashold: filters.cumulativeDataFilters?.reachThreashold ?? 1,
			marketThreashold:
				filters.cumulativeDataFilters?.marketThreashold ?? 100000000,
		},
		includeBundles: ensureArray(filters.includeBundles || uploaded.includeList),
		excludeBundles: ensureArray(filters.excludeBundles || uploaded.excludeList),
	};
};

// Normalize an already-built Filters DTO (e.g. one returned by an NLP endpoint)
// into the app-endpoint shape. Safe to run on a payload that is already app-shaped.
const adaptFiltersToAppApi = (apiFilters = {}) => {
	const next = { ...apiFilters };
	const { vectorFilters, includeDomains, excludeDomains } = next;
	delete next.vectorFilters;
	delete next.startIndex;
	delete next.includeDomains;
	delete next.excludeDomains;

	return {
		...next,
		domainFilters: {
			...(next.domainFilters ?? {}),
			distributionChannel: toAppDistributionChannel(
				next.domainFilters?.distributionChannel,
			),
		},
		appfilters: next.appfilters ?? buildAppFilters({}),
		tier_Details: next.tier_Details ??
			vectorFilters?.tier_Details ?? { tier: 0, keys: [] },
		cumulativeDataFilters: {
			perfomanceData:
				next.cumulativeDataFilters?.perfomanceData ?? "total_impressions",
			reachThreashold: next.cumulativeDataFilters?.reachThreashold ?? 1,
			marketThreashold:
				next.cumulativeDataFilters?.marketThreashold ?? 100000000,
		},
		includeBundles: ensureArray(next.includeBundles ?? includeDomains),
		excludeBundles: ensureArray(next.excludeBundles ?? excludeDomains),
	};
};

class CategoryApiService {
	constructor() {
		const env = getEnv();
		this.baseUrl = env.REACT_APP_API_URL;
		this.proxyUrl = env.REACT_APP_PROXY;
		this.apiUrl = this.proxyUrl;
		this.timeout = 300000;
	}

	getSharedAuthToken() {
		if (window.apiService?.tokens?.idToken) {
			return window.apiService.tokens.idToken;
		}
		return (
			localStorage.getItem("google_id_token") ||
			localStorage.getItem("authToken")
		);
	}

	parseJWT(token) {
		try {
			if (!token || typeof token !== "string") return null;
			const parts = token.split(".");
			if (parts.length !== 3) return null;
			const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				Array.from(atob(base64))
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join(""),
			);
			return JSON.parse(jsonPayload);
		} catch {
			return null;
		}
	}

	isTokenActuallyExpired(token) {
		if (!token) return true;
		try {
			const payload = this.parseJWT(token);
			if (!payload?.exp) return false;
			return Date.now() >= payload.exp * 1000 - 30000;
		} catch {
			return false;
		}
	}

	getHeaders() {
		const headers = {
			Accept: "application/json",
			"Content-Type": "application/json",
		};
		const token = this.getSharedAuthToken();
		if (token && !this.isTokenActuallyExpired(token)) {
			headers["Authorization"] = `Bearer ${token}`;
		}
		return headers;
	}

	async makeRequest(endpoint, options = {}) {
		const token = this.getSharedAuthToken();
		if (!token) {
			return { success: false, error: "Authentication required", requiresAuth: true };
		}

		const normalizedEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
		const url = `${this.apiUrl}${normalizedEndpoint}`;

		try {
			const response = await fetch(url, {
				method: "GET",
				...options,
				headers: { ...this.getHeaders(), ...options.headers },
				mode: "cors",
				credentials: "omit",
				signal: AbortSignal.timeout(this.timeout),
			});

			if (response.status === 401 || response.status === 403) {
				const text = await response.text().catch(() => "");
				return { success: false, error: `Auth error (${response.status}): ${text}`, requiresAuth: true };
			}

			if (!response.ok) {
				const errorText = await response.text().catch(() => "Unknown error");
				return {
					success: false,
					error: `HTTP ${response.status}: ${response.statusText}. ${errorText}`,
					statusCode: response.status,
				};
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			if (error.name === "AbortError" || error.name === "TimeoutError") {
				return { success: false, error: "Request timed out. Please try again." };
			}
			return { success: false, error: error.message || "Network error" };
		}
	}

	// POST /api/domains/category-search-text  (NLP → DomainSearchRequest)
	async fetchCategoryNlpQuery(inputText) {
		return this.makeRequest("domains/category-search-text", {
			method: "POST",
			body: JSON.stringify({
				inputText,
				searchType: "CATEGORY_DOMAIN",
			}),
		});
	}

	// POST /api/app-data/category-search-text  (NLP → app category search request)
	async fetchAppCategoryNlpQuery(inputText) {
		return this.makeRequest("app-data/category-search-text", {
			method: "POST",
			body: JSON.stringify({
				inputText,
				searchType: "CATEGORY_APP",
			}),
		});
	}

	// Two-step AI flow used by the AI Search dropdown:
	// 1. NLP endpoint matching the sub-tab → full search request
	//    - "domains" → domains/category-search-text  (CATEGORY_DOMAIN)
	//    - "apps"    → app-data/category-search-text (CATEGORY_APP)
	// 2. pass that payload to the matching search endpoint
	//    - "domains" → domains/category-search   (verbatim)
	//    - "apps"    → app-data/category-search  (normalized to the app Filters DTO)
	//
	// If the app NLP endpoint is unavailable we fall back to the domain NLP query —
	// the query[] / excludeQuery[] terms are identical; only the filter fields differ,
	// and adaptFiltersToAppApi reshapes those.
	async performCompleteCategorySearch(inputText, subTab = "domains") {
		let nlp;
		if (subTab === "apps") {
			nlp = await this.fetchAppCategoryNlpQuery(inputText);
			if (!nlp.success) {
				nlp = await this.fetchCategoryNlpQuery(inputText);
			}
		} else {
			nlp = await this.fetchCategoryNlpQuery(inputText);
		}
		if (!nlp.success) {
			return { success: false, error: nlp.error || "AI interpretation failed." };
		}

		const nlpPayload = nlp.data;
		const { query = [], excludeQuery = [] } = nlpPayload ?? {};
		// query may be empty if Gemini couldn't extract category concepts.
		// We still proceed: filters (geography, mediaSubtype, etc.) are applied
		// and surfaced in the sidebar. searchTerms chips are simply left empty.

		let endpoint;
		let requestBody;

		if (subTab === "apps") {
			// Normalize the NLP filters into the app Filters DTO: appfilters block,
			// tier_Details at the root (not under vectorFilters), no vectorFilters /
			// startIndex, bundle include/exclude lists.
			const { filters: nlpFilters = {}, ...rest } = nlpPayload;
			requestBody = {
				...rest,
				query: mapQueries(query),
				excludeQuery: mapQueries(excludeQuery),
				filters: adaptFiltersToAppApi(nlpFilters),
			};
			endpoint = "app-data/category-search";
		} else {
			// Pass the full NLP-generated DomainSearchRequest as-is to domains/category-search
			requestBody = nlpPayload;
			endpoint = "domains/category-search";
		}

		const searchResult = await this.makeRequest(endpoint, {
			method: "POST",
			body: JSON.stringify(requestBody),
		});

		if (!searchResult.success) {
			return { success: false, error: searchResult.error };
		}

		const data = searchResult.data;
		const ts = Date.now();

		return {
			success: true,
			data: {
				subTab,
				results: data.domain ?? data.data ?? [],
				totalRecords: data.totalRecords ?? data.totalCount ?? 0,
				keywords: Array.isArray(data.keywords) ? [...data.keywords] : [],
				searchTerms: [
					...query.map((q, i) => ({
						id: ts + i,
						text: q.query,
						threshold: q.threshold ?? DEFAULT_CATEGORY_QUERY_THRESHOLD,
						type: "include",
					})),
					...excludeQuery.map((q, i) => ({
						id: ts + 1000 + i,
						text: q.query,
						threshold: q.threshold ?? DEFAULT_CATEGORY_QUERY_THRESHOLD,
						type: "exclude",
					})),
				],
				appliedFilters: requestBody.filters ?? null,
				originalQuery: inputText,
			},
		};
	}

	// POST /api/domains/category-search
	async searchDomainsByCategory({
		queries = [],
		excludeQueries = [],
		filters = {},
		sortBy = "relevance",
		view = "DESC",
	}) {
		const payload = {
			query: mapQueries(queries),
			excludeQuery: mapQueries(excludeQueries),
			filters: mapUiFiltersToApi(filters),
			sortBy,
			view,
		};

		return this.makeRequest("domains/category-search", {
			method: "POST",
			body: JSON.stringify(payload),
		});
	}

	// POST /api/app-data/category-search
	async searchAppsByCategory({
		queries = [],
		excludeQueries = [],
		filters = {},
		sortBy = "relevance",
		view = "DESC",
	}) {
		const apiFilters = mapUiFiltersToAppApi(filters);

		const payload = {
			query: mapQueries(queries),
			excludeQuery: mapQueries(excludeQueries),
			filters: apiFilters,
			sortBy,
			view,
		};

		return this.makeRequest("app-data/category-search", {
			method: "POST",
			body: JSON.stringify(payload),
		});
	}
}

const categoryApiService = new CategoryApiService();
export default categoryApiService;
